import type { ReactNode } from 'react';
import { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router';
import { useAccountSelectionApiFacade } from '../../apis/facades/useAccountSelectionApiFacade';
import type { UserAccount } from '@server/user-account';
import { selectUserAccountId } from '../../reducers/userInfoReducer';
import useSweepNavigate from '../../navigation-utils/useSweepNavigate';
import { replaceAccountIdInPath } from '../../navigation-utils/urlOrgUtils';
import { useSweepNotifications } from '../notifications/useSweepNotifications';
import { SweepNotificationVariant } from '../../reducers/notificationsReducer';
import { extractShortUUID } from '../../navigation-utils/readableUUID';
import { telemetry } from '../../telemetry';

interface PendingAccountSwitch {
  authZeroOrgId: string;
  returnTo: string;
}

interface AccountSelectionContextType {
  isOpen: boolean;
  accounts: UserAccount[] | undefined;
  isLoadingAccounts: boolean;
  isAccountReady: boolean;
  isSSOUser: boolean;
  setIsSSOUser: (isSSOUser: boolean) => void;
  openAccountSelection: () => void;
  closeAccountSelection: () => void;
  fetchAccounts: () => Promise<void>;
  removeAccountFromState: (accountId: string) => void;
  // Auto-switch signal: set when the URL points at an account the user has access
  // to but is not the one currently in their session. Consumed by
  // AccountAutoSwitchEffect (which lives inside Auth0Provider) to trigger a real
  // /authorize redirect — required because refresh_token cannot establish a new
  // org_id. See AUTHENTICATION.md §6.
  pendingAccountSwitch: PendingAccountSwitch | null;
  clearPendingAccountSwitch: () => void;
}

export const AccountSelectionContext = createContext<AccountSelectionContextType | undefined>(
  undefined,
);

export const AccountSelectionProvider = ({ children }: { children: ReactNode }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [accounts, setAccounts] = useState<UserAccount[] | undefined>(undefined);
  const [isLoadingAccounts, setIsLoadingAccounts] = useState(false);
  const [isAccountReady, setIsAccountReady] = useState(false);
  const [isSSOUser, setIsSSOUser] = useState(false);
  const [pendingAccountSwitch, setPendingAccountSwitch] = useState<PendingAccountSwitch | null>(
    null,
  );
  // Tracks which authZeroOrgId we've already requested a switch for. Prevents
  // the validation effect from re-firing the switch after the bridge component
  // (AccountAutoSwitchEffect) clears the pending state. Using a ref (not state)
  // keeps it out of the effect's dependency array.
  const firedSwitchForOrgRef = useRef<string | null>(null);

  const { get_accounts } = useAccountSelectionApiFacade();
  const params = useParams<{ accountId: string }>();
  const readableAccountId = params.accountId;
  const userAccountId = useSelector(selectUserAccountId);
  const location = useLocation();
  const { navigate } = useSweepNavigate();
  const { addNotification } = useSweepNotifications();

  // Resolve fullAccountId from readableAccountId (similar logic to useSweepParams but
  // we cannot use it as it takes stuff from this context)
  let fullAccountId: string | undefined;
  if (readableAccountId && accounts) {
    if (readableAccountId.length !== 36) {
      const shortAccountId = extractShortUUID(readableAccountId);
      if (shortAccountId) {
        const matchingAccount = accounts.find((account) => account.id.startsWith(shortAccountId));
        fullAccountId = matchingAccount?.id;
      }
    } else if (readableAccountId && readableAccountId.length === 36) {
      const account = accounts.find((account) => account.id === readableAccountId);
      fullAccountId = account?.id;
    }
  }

  const openAccountSelection = useCallback(() => setIsOpen(true), []);
  const closeAccountSelection = useCallback(() => setIsOpen(false), []);
  const clearPendingAccountSwitch = useCallback(() => setPendingAccountSwitch(null), []);

  const fetchAccounts = useCallback(async () => {
    setIsLoadingAccounts(true);
    try {
      const accounts = await get_accounts();
      const accountsWithNames = accounts.map((account) => ({
        ...account,
        name: !!account.name ? account.name : 'Untitled',
      }));
      setAccounts(accountsWithNames);
    } catch (error) {
      telemetry.captureError(error, { message: 'Failed to fetch accounts' });
      setAccounts([]);
    } finally {
      setIsLoadingAccounts(false);
    }
  }, [get_accounts]);

  const removeAccountFromState = useCallback(
    async (accountId: string) => {
      setAccounts(accounts?.filter((account) => account.id !== accountId));
    },
    [accounts],
  );

  // Fetch accounts if there's an accountId in URL that needs validation
  useEffect(() => {
    if (readableAccountId && !accounts && userAccountId) {
      fetchAccounts();
    }
  }, [readableAccountId, accounts, userAccountId, fetchAccounts]);

  // Validate account after accounts are loaded
  useEffect(() => {
    // Wait for userAccountId to be available
    if (!userAccountId) {
      setIsAccountReady(false);
      return;
    }

    if (!accounts) {
      // If there's an accountId in the URL, we MUST wait for accounts to validate it
      if (readableAccountId) {
        setIsAccountReady(false);
        return;
      }
      // No accountId in URL - safe to proceed without accounts
      setIsAccountReady(true);
      return;
    }

    // Handle account switching if URL accountId differs from current user's account
    if (readableAccountId) {
      // Case 1: fullAccountId is undefined - account not found or invalid
      if (!fullAccountId) {
        addNotification({
          message: "You don't have access to given account or it's invalid",
          variant: SweepNotificationVariant.Error,
        });

        const newPath = replaceAccountIdInPath(location.pathname, userAccountId, readableAccountId);
        navigate(newPath, { replace: true });
        setIsAccountReady(false);
        return;
      }

      // Case 2: fullAccountId exists but is different from current user's account
      if (fullAccountId !== userAccountId) {
        const targetAccount = accounts.find((account) => account.id === fullAccountId);

        if (targetAccount) {
          // We can't call loginWithRedirect here because Auth0Provider is a
          // descendant of AccountSelectionProvider in the tree. Instead, signal
          // the pending switch and let AccountAutoSwitchEffect (mounted inside
          // Auth0Provider) trigger the real /authorize redirect.
          //
          // A plain page reload with `?organization=X` does NOT establish org_id
          // in the access token because Auth0's silent-token cache key does not
          // include `organization`; only a fresh /authorize with `organization`
          // in authorizationParams mints an org-scoped token.
          // See AUTHENTICATION.md §6.
          if (firedSwitchForOrgRef.current !== targetAccount.authZeroOrgId) {
            firedSwitchForOrgRef.current = targetAccount.authZeroOrgId;
            const returnTo =
              location.pathname +
              location.search.replace(/([?&])organization=[^&]*(&|$)/, (_m, p1, p2) =>
                p2 === '&' ? p1 : '',
              ) +
              location.hash;
            setPendingAccountSwitch({
              authZeroOrgId: targetAccount.authZeroOrgId,
              returnTo: returnTo || '/',
            });
          }
          setIsAccountReady(false);
          return;
        } else {
          addNotification({
            message: "You don't have access to given account or it's invalid",
            variant: SweepNotificationVariant.Error,
          });

          const newPath = replaceAccountIdInPath(
            location.pathname,
            userAccountId,
            readableAccountId,
          );
          navigate(newPath, { replace: true });
          setIsAccountReady(false);
          return;
        }
      }
    }

    // If we reach here, account validation passed
    setIsAccountReady(true);
  }, [
    userAccountId,
    readableAccountId,
    fullAccountId,
    accounts,
    location.pathname,
    location.search,
    location.hash,
    navigate,
    addNotification,
  ]);

  return (
    <AccountSelectionContext.Provider
      value={{
        isOpen,
        accounts,
        isLoadingAccounts,
        isAccountReady,
        isSSOUser,
        openAccountSelection,
        closeAccountSelection,
        fetchAccounts,
        removeAccountFromState,
        setIsSSOUser,
        pendingAccountSwitch,
        clearPendingAccountSwitch,
      }}
    >
      {children}
    </AccountSelectionContext.Provider>
  );
};

export const useAccountSelectionContext = () => {
  const context = useContext(AccountSelectionContext);
  if (!context) {
    throw new Error('useAccountSelectionContext must be used within AccountSelectionProvider');
  }
  return context;
};
